import { TextField } from "@mui/material";
import { AccessKeys, VK_ENTER } from "app/appConstants";
import { IconButtonSearch } from "components/iconButtons";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { useMprcTheme } from "components/MprcThemeProvider";
import { useSimpleDebounce } from "hooks/useDebounce";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

export type MprcSearchTextFieldConfirmMode = "onEnter" | "immediate" | "debounce";

interface IProps {
    initialValue?: string;
    placeholder: string;
    disabled?: boolean;
    autoFocus?: boolean;
    searchIcon?: boolean;
    confirmMode?: MprcSearchTextFieldConfirmMode;
    onConfirm: (value: string, enterPressed: boolean) => void;
}

export type MprcSearchTextFieldHandle = {
    setFocus: () => void;
    reset: () => void;
    replaceSearchText: (value?: string) => void;
};


const MprcSearchTextFieldComponent: React.ForwardRefRenderFunction<MprcSearchTextFieldHandle, IProps> = (props, forwardRef) => {

    const { initialValue, placeholder, autoFocus, disabled, confirmMode = "onEnter", searchIcon: showSearchIcon = false, onConfirm } = props;
    const { theme } = useMprcTheme();
    const [searchText, setSearchText] = useState<string>(initialValue ?? "");
    const inputRef = useRef<HTMLInputElement>(undefined);
    const debouncedSearchText = useSimpleDebounce<string>(searchText, 500);
    const previousSearchText = useRef<string>(initialValue ?? "");

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        setFocus,
        reset,
        replaceSearchText
    }));

    const setFocus = () => {
        inputRef.current?.focus();
    };

    const reset = () => {
        if (autoFocus) setFocus();
        inputRef.current!.value = previousSearchText.current = initialValue ?? "";
        setSearchText(initialValue ?? "");
        onConfirm("", false);
    };

    const replaceSearchText = (value?: string) => {
        setSearchText(value ?? "");
        previousSearchText.current = value ?? "";
    };

    const doConfirmChange = (searchText: string, enterPressed: boolean) => {
        var st = searchText.trim();
        if (st == "" && previousSearchText.current == "") {
            setSearchText("");
        }
        else {
            previousSearchText.current = st;
            onConfirm(st, enterPressed);
        }
    };
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if ((initialValue == undefined || initialValue == "") && searchText.length > 0) {
            setSearchText("");
            previousSearchText.current = "";
        }
    }, [initialValue]);

    useDidUpdateEffect(() => {
        if (confirmMode == "debounce") doConfirmChange(debouncedSearchText, false);
    }, [debouncedSearchText]);

    return (
        <TextField
            fullWidth
            autoFocus={autoFocus}
            placeholder={placeholder}
            autoComplete="off"
            autoCorrect="off"
            disabled={disabled}
            value={searchText}
            onChange={
                (e) => {
                    setSearchText(e.target.value);
                    if (confirmMode == "immediate") {
                        doConfirmChange(e.target.value.trim(), false);
                    }
                }
            }
            onKeyDown={(e) => e.key == VK_ENTER && doConfirmChange(searchText, true)}
            inputRef={(ref) => inputRef.current = ref}
            accessKey={AccessKeys.Search}
            slotProps={{
                input: {
                    endAdornment: searchText.length > 0
                        ? <IconButtonClearSearch
                            size="small"
                            color="inherit"
                            title={theme.MprcSearchTextField.clearTooltip}
                            onClick={() => {
                                onConfirm("", false);
                                setSearchText("");
                                previousSearchText.current = "";
                                inputRef.current?.focus();
                            }}
                        />
                        : showSearchIcon && <IconButtonSearch
                            size="small"
                            color="inherit"
                        />
                }
            }}
        />
    );
};

export const MprcSearchTextField = React.forwardRef(MprcSearchTextFieldComponent);
